import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * TransfersList — Ownership Transfers page
 *
 * Route: /inventory/transfers
 *
 * Shows all ownership transfers for the caller's organisation.
 * OEM users see all transfers system-wide.
 * Org users see transfers where their org was sender OR receiver.
 *
 * Features:
 *   - DataGrid with server-side pagination + sort
 *   - Filter by transfer_type, date range
 *   - Row click → navigate to asset detail
 *   - Transfer detail drawer (inline, no separate page needed)
 *   - Stat cards: Total / OEM Sales / Reseller Sales / RMA Returns
 *
 * APIs:
 *   GET /api/v1/inventory/transfers          (list)
 *   GET /api/v1/inventory/transfers/:id      (detail drawer)
 */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  Chip,
  Alert,
  Skeleton,
  Button,
  Stack,
  MenuItem,
  useTheme,
  InputAdornment,
  Collapse,
  Drawer,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Icons
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import SellIcon from "@mui/icons-material/Sell";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import FactoryIcon from "@mui/icons-material/Factory";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../../../auth/useAuth";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

// ─── Transfer type styling ────────────────────────────────────────────────────

const TRANSFER_TYPE_STYLES = {
  OEM_SALE: {
    label: "OEM Sale",
    color: "#1d4ed8",
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
  DISTRIBUTOR_SALE: {
    label: "Distributor Sale",
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
  RESELLER_SALE: {
    label: "Reseller Sale",
    color: "#065f46",
    bg: "#ecfdf5",
    border: "#a7f3d0",
  },
  RMA_RETURN: {
    label: "RMA Return",
    color: "#92400e",
    bg: "#fff7ed",
    border: "#fde68a",
  },
};

const typeStyle = (t) =>
  TRANSFER_TYPE_STYLES[t] || {
    label: t,
    color: "#374151",
    bg: "#f3f4f6",
    border: "#e5e7eb",
  };

const formatOrg = (org, fallbackType) => {
  if (org?.name && org?.code) return `${org.name} (${org.code})`;
  if (org?.name) return org.name;
  if (fallbackType === "OEM") return "OEM (no org)";
  return "—";
};

const formatUser = (user, fallbackId) => {
  if (user?.name && user?.email) return `${user.name} (${user.email})`;
  if (user?.name) return user.name;
  if (user?.email) return user.email;
  return fallbackId || "—";
};

// ─── Ownership state badge ────────────────────────────────────────────────────

const OWNERSHIP_COLORS = {
  OEM_OWNED: { label: "OEM Owned", color: "#1d4ed8", bg: "#eff6ff" },
  OWNED_BY_PARTNER: {
    label: "Owned by Partner",
    color: "#7c3aed",
    bg: "#f5f3ff",
  },
  OWNED_BY_CUSTOMER: {
    label: "Owned by Customer",
    color: "#065f46",
    bg: "#ecfdf5",
  },
  RMA_RETURNED: { label: "RMA Returned", color: "#92400e", bg: "#fff7ed" },
};

function OwnerBadge({ state }) {
  const s = OWNERSHIP_COLORS[state] || {
    label: state,
    color: "#374151",
    bg: "#f3f4f6",
  };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        bgcolor: s.bg,
        color: s.color,
        fontWeight: 600,
        borderRadius: "6px",
        fontSize: 11,
      }}
    />
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

function TransferDetailDrawer({
  transferId,
  token,
  activeOrg,
  onClose,
  navigate,
}) {
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!transferId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE}/transfers/${transferId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
        },
      })
      .then((res) => {
        setTransfer(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to load transfer");
        setLoading(false);
      });
  }, [transferId]);

  const rows = transfer
    ? [
        { label: "Transfer ID", value: transfer.id, mono: true },
        {
          label: "Transfer type",
          value:
            TRANSFER_TYPE_STYLES[transfer.transfer_type]?.label ||
            transfer.transfer_type,
        },
        { label: "From owner", value: transfer.from_owner_type },
        {
          label: "From org",
          value: formatOrg(transfer.from_owner_org, transfer.from_owner_type),
        },
        { label: "To owner", value: transfer.to_owner_type },
        {
          label: "To org",
          value: formatOrg(transfer.to_owner_org, transfer.to_owner_type),
        },
        { label: "Status", value: transfer.transfer_status },
        {
          label: "Initiated by",
          value: formatUser(transfer.initiated_by_user, transfer.initiated_by),
        },
        {
          label: "Transferred at",
          value: transfer.transferred_at
            ? new Date(transfer.transferred_at).toLocaleString("en-IN")
            : "—",
        },
        { label: "Purchase order", value: transfer.purchase_order || "—" },
        { label: "Invoice number", value: transfer.invoice_number || "—" },
        { label: "Remarks", value: transfer.remarks || "—" },
      ]
    : [];

  return (
    <Box
      sx={{
        width: { xs: "100vw", sm: 420 },
        maxWidth: "100vw",
        p: 0,
        height: "100%",
        overflowY: "auto",
      }}
    >
      {/* Drawer header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={{ xs: 2, sm: 3 }}
        py={2}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ color: "#e2f8f5" }}
        >
          Transfer detail
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            mt: -0.5,
            color: "rgba(203, 213, 225, 0.80)",
            "&:hover": {
              color: "#e2f8f5",
              background: "rgba(255,255,255,0.08)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box px={{ xs: 2, sm: 3 }} py={2}>
        {loading && (
          <Stack spacing={1.5}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="text"
                width={i % 2 === 0 ? "70%" : "50%"}
              />
            ))}
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {transfer && !loading && (
          <>
            {/* Asset context */}
            {(transfer.asset_serial_number || transfer.asset) && (
              <Box
                mb={2.5}
                p={2}
                borderRadius={2}
                sx={{
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  mb={0.5}
                  textTransform="uppercase"
                  letterSpacing={0.5}
                >
                  Asset
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {transfer.asset_serial_number ||
                    transfer.asset?.serial_number}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {transfer.asset_type || transfer.asset?.asset_type}
                  {transfer.asset_model || transfer.asset?.model
                    ? ` · ${transfer.asset_model || transfer.asset?.model}`
                    : ""}
                </Typography>
                <Box mt={1}>
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() =>
                      navigate(
                        `/inventory/assets/${transfer.inventory_asset_id}`,
                      )
                    }
                    sx={{ textTransform: "none", fontSize: 12, p: 0 }}
                  >
                    View asset
                  </Button>
                </Box>
              </Box>
            )}

            {/* Transfer type badge */}
            <Box mb={2}>
              {(() => {
                const s = typeStyle(transfer.transfer_type);
                return (
                  <Chip
                    label={s.label}
                    size="small"
                    sx={{
                      bgcolor: s.bg,
                      color: s.color,
                      fontWeight: 600,
                      border: `1px solid ${s.border}`,
                      borderRadius: "6px",
                    }}
                  />
                );
              })()}
            </Box>

            {/* Detail rows */}
            {rows.map((row, i) => (
              <Box
                key={i}
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                py={1}
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 0.5, sm: 0 },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    flexShrink: 0,
                    minWidth: { xs: "unset", sm: 120 },
                    fontWeight: { xs: 600, sm: 400 },
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  textAlign={{ xs: "left", sm: "right" }}
                  sx={{
                    fontFamily: row.mono ? "monospace" : "inherit",
                    fontSize: row.mono ? 11 : 12,
                    wordBreak: "break-all",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

// ─── Custom DataGrid toolbar ──────────────────────────────────────────────────

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Box display="flex" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ fileName: "ownership_transfers" }} />
      </Box>
    </GridToolbarContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function TransfersList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { permissions, activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const orgType = (activeOrg?.type || "").toUpperCase();
  const isPlatformOwner =
    permissions?.has?.("*") || permissions?.has?.("system.manage");
  const isDistributor = orgType === "DISTRIBUTOR";
  const isReseller = orgType === "RESELLER";
  const isCustomer = orgType === "CUSTOMER";

  // ── State ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([
    { field: "transferred_at", sort: "desc" },
  ]);

  // Filters
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const filteredTransfers = transfers.filter((row) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return Object.values(row).some((val) =>
      String(val ?? "")
        .toLowerCase()
        .includes(q),
    );
  });

  // Drawer
  const [drawerTransferId, setDrawerTransferId] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    oemSale: 0,
    resellerSale: 0,
    rmaReturn: 0,
    loading: true,
  });

  const hasPermission = (p) => {
    if (!permissions || permissions.size === 0) return false;
    if (permissions.has("*")) return true;
    return permissions.has(p);
  };

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  // ── Fetch transfers ──
  const fetchTransfers = useCallback(async () => {
    if (!hasPermission("inventory.transfer.view")) return;
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        sort: sortModel[0]?.field || "transferred_at",
        order: sortModel[0]?.sort || "desc",
        ...(typeFilter && { transfer_type: typeFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      };
      const res = await axios.get(`${API_BASE}/transfers`, {
        headers: authHeaders,
        params,
      });
      setTransfers(res.data.data || []);
      setRowCount(res.data.meta?.pagination?.total_items || 0);
      setError(null);
    } catch (err) {
      setError("Failed to load transfers.");
    } finally {
      setLoading(false);
    }
  }, [
    token,
    activeOrg,
    paginationModel,
    sortModel,
    typeFilter,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  // ── Fetch stats (4 parallel) ──
  const fetchStats = useCallback(() => {
    if (!hasPermission("inventory.transfer.view")) return;
    setStats((prev) => ({ ...prev, loading: true }));
    Promise.all([
      axios.get(`${API_BASE}/transfers`, {
        headers: authHeaders,
        params: { per_page: 1 },
      }),
      axios.get(`${API_BASE}/transfers`, {
        headers: authHeaders,
        params: { per_page: 1, transfer_type: "OEM_SALE" },
      }),
      axios.get(`${API_BASE}/transfers`, {
        headers: authHeaders,
        params: { per_page: 1, transfer_type: "RESELLER_SALE" },
      }),
      axios.get(`${API_BASE}/transfers`, {
        headers: authHeaders,
        params: { per_page: 1, transfer_type: "RMA_RETURN" },
      }),
    ])
      .then(([t, o, r, rma]) =>
        setStats({
          total: t.data.meta?.pagination?.total_items ?? 0,
          oemSale: o.data.meta?.pagination?.total_items ?? 0,
          resellerSale: r.data.meta?.pagination?.total_items ?? 0,
          rmaReturn: rma.data.meta?.pagination?.total_items ?? 0,
          loading: false,
        }),
      )
      .catch(() => setStats((prev) => ({ ...prev, loading: false })));
  }, [token, activeOrg]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const hasActiveFilters = typeFilter || dateFrom || dateTo;

  const clearFilters = () => {
    setTypeFilter("");
    setDateFrom("");
    setDateTo("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // ── Columns ──
  const columns = [
   {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "transfer_type",
      headerName: "Type",
      flex: 1.1,
      minWidth: 150,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const s = typeStyle(params.value);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Chip
              label={s.label}
              size="small"
              sx={{
                bgcolor: s.bg,
                color: s.color,
                fontWeight: 600,
                border: `1px solid ${s.border}`,
                borderRadius: "6px",
                fontSize: 11,
                height: 24,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "asset_serial_number",
      headerName: "Serial Number",
      flex: 1.2,
      minWidth: 140,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              fontWeight: 600,
              color: "primary.main",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.value || params.row.inventory_asset_id?.slice(0, 8) + "…"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "from_owner_type",
      headerName: "From",

      flex: 1.2,
      minWidth: 170,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: 12 }}
        >
          {formatOrg(params.row.from_owner_org, params.value)}
        </Typography>
      ),
    },
    {
      field: "to_owner_type",
      headerName: "To",

      flex: 1.2,
      minWidth: 170,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: 12 }}
        >
          {formatOrg(params.row.to_owner_org, params.value)}
        </Typography>
      ),
    },
    {
      field: "transfer_status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 90,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Chip
            label={params.value}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: 10, height: 22 }}
          />
        </Box>
      ),
    },
    {
      field: "purchase_order",
      headerName: "PO Number",
      flex: 0.9,
      minWidth: 110,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            color="text.secondary"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "transferred_at",
      headerName: "Date",
      flex: 1,
      minWidth: 130,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            color="text.secondary"
            sx={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {params.value
              ? new Date(params.value).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Typography>
        </Box>
      ),
    },
  ];

  // ── Guard ──
  if (!hasPermission("inventory.transfer.view")) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          You do not have permission to view ownership transfers.
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: isPlatformOwner ? "Total transfers" : "Visible handoffs",
      value: stats.total,
      icon: <SwapHorizIcon sx={{ color: "#7dd3fc" }} />,
      bg: "rgba(56, 189, 248, 0.12)",
      border: "rgba(56, 189, 248, 0.25)",
    },
    {
      title: isPlatformOwner
        ? "OEM sales"
        : isDistributor || isReseller
          ? "Inbound receipts"
          : "Upstream receipts",
      value: stats.oemSale,
      icon: <FactoryIcon sx={{ color: "#c4b5fd" }} />,
      bg: "rgba(167, 139, 250, 0.12)",
      border: "rgba(167, 139, 250, 0.25)",
    },
    {
      title: isPlatformOwner
        ? "Reseller sales"
        : isDistributor
          ? "Outbound sales"
          : isReseller
            ? "Customer sales"
            : "Partner sales",
      value: stats.resellerSale,
      icon: <StorefrontIcon sx={{ color: "#5eead4" }} />,
      bg: "rgba(20, 184, 166, 0.12)",
      border: "rgba(20, 184, 166, 0.25)",
    },
    {
      title: isPlatformOwner ? "RMA returns" : "Service returns",
      value: stats.rmaReturn,
      icon: <AssignmentReturnIcon sx={{ color: "#fcd34d" }} />,
      bg: "rgba(251, 191, 36, 0.12)",
      border: "rgba(251, 191, 36, 0.25)",
    },
  ];

  return (
    <Box
      sx={{
        margin: "0 auto",
        p: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
      }}
    >
      {/* ── Header ── */}

      <Box
        sx={{
          border: "1px solid rgba(148, 163, 184, 0.18)",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          borderRadius: 3,
          p: 3,
          mb: 4,
        }}
      >
        {/* ── Page header ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={4}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: "#e2f8f5" ,fontSize:{xs:28,md:40} }}>
              {isPlatformOwner ? "Ownership Transfers" : "Inventory Handoffs"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(203, 213, 225, 0.80)" }}
            >
              {isPlatformOwner
                ? "Immutable audit trail of every ownership change across the supply chain."
                : isDistributor
                  ? "Track stock received from OEM and handoffs made onward to reseller or customer accounts."
                  : isReseller
                    ? "Review stock received into your channel and downstream sales to customer accounts."
                    : isCustomer
                      ? "Review inbound ownership receipts and service-return history in your workspace."
                      : "Track inbound and outbound ownership handoffs involving your current workspace."}
            </Typography>
          </Box>
        </Stack>

        {!isPlatformOwner && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            This workspace shows only transfers where your organization is part
            of the commercial custody chain.
          </Alert>
        )}

        {/* ── Divider ── */}
        <Box
          sx={{ borderBottom: "1px solid rgba(148, 163, 184, 0.15)", mb: 3 }}
        />

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.28)",
              color: "#fca5a5",
              "& .MuiAlert-icon": { color: "#fca5a5" },
            }}
          >
            {error}
          </Alert>
        )}

        {/* ── Stat cards ── */}
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          gap={2}
        >
          {stats.loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid rgba(148, 163, 184, 0.15)",
                    background: "rgba(255, 255, 255, 0.04)",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={52}
                    height={52}
                    sx={{
                      mr: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(148, 163, 184, 0.10)",
                    }}
                  />
                  <Box flex={1}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      sx={{ bgcolor: "rgba(148, 163, 184, 0.10)" }}
                    />
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={32}
                      sx={{ bgcolor: "rgba(148, 163, 184, 0.10)" }}
                    />
                  </Box>
                </Card>
              ))
            : statCards.map((s, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid rgba(148, 163, 184, 0.15)",
                    background: "rgba(255, 255, 255, 0.04)",
                    p: 2,
                    transition: "background 0.2s, border-color 0.2s",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.07)",
                      borderColor: "rgba(148, 163, 184, 0.30)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: s.bg,
                        border: `1px solid ${s.border}`,
                        mr: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: "rgba(203, 213, 225, 0.75)" }}
                      >
                        {s.title}
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: "#e2f8f5" }}
                      >
                        {s.value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
        </Box>
      </Box>

      {/* ── Table card ── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Filters */}
        <Box
          p={2}
          borderBottom="1px solid rgba(148, 163, 184, 0.18)"
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={2}
          sx={{
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <TextField
            select
            size="small"
            label="Transfer type"
            value={typeFilter}
            onChange={handleFilterChange(setTypeFilter)}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="OEM_SALE">OEM Sale</MenuItem>
            <MenuItem value="DISTRIBUTOR_SALE">Distributor Sale</MenuItem>
            <MenuItem value="RESELLER_SALE">Reseller Sale</MenuItem>
            <MenuItem value="RMA_RETURN">RMA Return</MenuItem>
          </TextField>

          <TextField
            size="small"
            label="From date"
            type="date"
            value={dateFrom}
            onChange={handleFilterChange(setDateFrom)}
             sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& input[type='date']::-webkit-calendar-picker-indicator": {
                filter: "invert(1) opacity(0.6)",
                cursor: "pointer",
              },
            }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            size="small"
            label="To date"
            type="date"
            value={dateTo}
            onChange={handleFilterChange(setDateTo)}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& input[type='date']::-webkit-calendar-picker-indicator": {
                filter: "invert(1) opacity(0.6)",
                cursor: "pointer",
              },
            }}
            InputLabelProps={{ shrink: true }}
          />

          {hasActiveFilters && (
            <Button
              size="small"
              variant="outlined"
              onClick={clearFilters}
              sx={{
                textTransform: "none",
                fontSize: 12,
                height: 38,
                color: "#fca5a5",
                borderColor: "rgba(239, 68, 68, 0.35)",
                background: "rgba(239, 68, 68, 0.10)",
                "&:hover": {
                  borderColor: "rgba(239, 68, 68, 0.55)",
                  background: "rgba(239, 68, 68, 0.18)",
                },
              }}
            >
              Clear filters
            </Button>
          )}

          {/* Search bar — after all filters, pushes to the right on desktop */}
          <TextField
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              width: { xs: "100%", md: 170 },
              ml: { xs: 0, sm: "auto" },
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(148, 163, 184, 0.55)",
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery("")}
                    sx={{
                      color: "rgba(203, 213, 225, 0.60)",
                      p: 0.3,
                      "&:hover": {
                        color: "#e2f8f5",
                        background: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {/* DataGrid */}
        <Box
          sx={{
            height: transfers.length === 0 && !loading ? 320 : 620,
            width: "100%",
          }}
        >
          <DataGrid
            rows={filteredTransfers}
            rowCount={rowCount}
            loading={loading}
            paginationMode="server"
            sortingMode="server"
            pageSizeOptions={[15, 20, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(params) => setDrawerTransferId(params.row.id)}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={6}
                >
                  <SwapHorizIcon
                    sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    gutterBottom
                  >
                    {hasActiveFilters
                      ? "No transfers match these filters"
                      : "No transfers recorded yet"}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    {hasActiveFilters
                      ? "Try adjusting the filters above"
                      : "Transfers appear here as assets move through the supply chain"}
                  </Typography>
                </Box>
              ),
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "text.secondary",
                fontSize: 12,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#334155" : "#f8fafc",
                cursor: "pointer",
              },
              "& .MuiDataGrid-cell:focus": { outline: "none" },
              "& .MuiDataGrid-cell:focus-within": { outline: "none" },
            }}
          />
        </Box>
      </Card>

      {/* ── Transfer detail drawer ── */}
      <Drawer
        anchor="right"
        open={!!drawerTransferId}
        onClose={() => setDrawerTransferId(null)}
        PaperProps={{ sx: { borderRadius: "12px 0 0 12px" } }}
      >
        <TransferDetailDrawer
          transferId={drawerTransferId}
          token={token}
          activeOrg={activeOrg}
          onClose={() => setDrawerTransferId(null)}
          navigate={navigate}
        />
      </Drawer>
    </Box>
  );
}
